import { Spin, Empty, Typography, Button, Modal, Form, Input, Table, Space, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDate } from '../../helpers/date';

interface RadBotNodeTableDataType {
  key: number;
  intPkRadBotNodeID: number;
  radBotToken: string;
  radBotName: string;
  radBotIP: string;
  radBotAuthPort: number;
  radBotAcctPort: number;
  dateCreated: string;
}

const RadBotNodesComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [createForm] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedRadBotNode, setSelectedRadBotNode] = useState<RadBotNodeTableDataType | null>(null);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('radbot-nodes/all');

  const [
    createRadBotNodeData,
    createRadBotNodeResponse,
    createRadBotNodeError,
    createRadBotNodeLoading,
    createRadBotNodeMessage,
    createRadBotNodeErrorCount,
  ] = useAxiosPost<ResponseProp>('radbot-nodes/create');

  const [
    updateRadBotNodeData,
    updateRadBotNodeResponse,
    updateRadBotNodeError,
    updateRadBotNodeLoading,
    updateRadBotNodeMessage,
    updateRadBotNodeErrorCount,
  ] = useAxiosPost<ResponseProp>('radbot-nodes/update');

  const [allRadBotNodes, setAllRadBotNodes] = useState<RadBotNodeTableDataType[]>([]);
  const postApiDataRef = useRef(postApiData);
  const hasProcessedCreateSuccess = useRef(false);
  const hasProcessedUpdateSuccess = useRef(false);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0, update: 0 });
  const lastProcessedCreateResponse = useRef<any>(null);
  const lastProcessedUpdateResponse = useRef<any>(null);

  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        const radBotNodesWithKeys = responseData.map((node: any) => ({
          ...node,
          key: node.intPkRadBotNodeID,
        }));
        setAllRadBotNodes(radBotNodesWithKeys);
      } else {
        setAllRadBotNodes([]);
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.api !== apiErrorCount) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification('error', 'Failed to Load RadBot Nodes', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create radbot node success
  useEffect(() => {
    if (createRadBotNodeResponse !== null && createRadBotNodeResponse !== lastProcessedCreateResponse.current) {
      lastProcessedCreateResponse.current = createRadBotNodeResponse;
      hasProcessedCreateSuccess.current = true;
      lastProcessedErrorCount.current.create = createRadBotNodeErrorCount;
      openNotification('success', 'RadBot Node Created', 'RadBot node created successfully');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createRadBotNodeResponse]);

  useEffect(() => {
    if (createRadBotNodeResponse === null) {
      hasProcessedCreateSuccess.current = false;
      lastProcessedCreateResponse.current = null;
    }
  }, [createRadBotNodeResponse]);

  useEffect(() => {
    if (
      createRadBotNodeError !== null &&
      createRadBotNodeErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createRadBotNodeErrorCount &&
      !hasProcessedCreateSuccess.current
    ) {
      lastProcessedErrorCount.current.create = createRadBotNodeErrorCount;
      openNotification('error', 'Failed to Create RadBot Node', createRadBotNodeError);
    }
  }, [createRadBotNodeError, createRadBotNodeErrorCount, openNotification]);

  // Handle update radbot node success
  useEffect(() => {
    if (updateRadBotNodeResponse !== null && updateRadBotNodeResponse !== lastProcessedUpdateResponse.current) {
      lastProcessedUpdateResponse.current = updateRadBotNodeResponse;
      hasProcessedUpdateSuccess.current = true;
      lastProcessedErrorCount.current.update = updateRadBotNodeErrorCount;
      openNotification('success', 'RadBot Node Updated', 'RadBot node updated successfully');
      setIsUpdateModalOpen(false);
      updateForm.resetFields();
      setSelectedRadBotNode(null);
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [updateRadBotNodeResponse]);

  useEffect(() => {
    if (updateRadBotNodeResponse === null) {
      hasProcessedUpdateSuccess.current = false;
      lastProcessedUpdateResponse.current = null;
    }
  }, [updateRadBotNodeResponse]);

  useEffect(() => {
    if (
      updateRadBotNodeError !== null &&
      updateRadBotNodeErrorCount !== 0 &&
      lastProcessedErrorCount.current.update !== updateRadBotNodeErrorCount &&
      !hasProcessedUpdateSuccess.current
    ) {
      lastProcessedErrorCount.current.update = updateRadBotNodeErrorCount;
      openNotification('error', 'Failed to Update RadBot Node', updateRadBotNodeError);
    }
  }, [updateRadBotNodeError, updateRadBotNodeErrorCount, openNotification]);

  const handleCreate = (values: any) => {
    const radBotNodeData = {
      radBotName: values.radBotName.trim(),
      radBotIP: values.radBotIP.trim(),
      radBotAuthPort: Number(values.radBotAuthPort),
      radBotAcctPort: Number(values.radBotAcctPort),
    };
    createRadBotNodeData(radBotNodeData as any);
  };

  const handleUpdate = (values: any) => {
    if (!selectedRadBotNode) return;
    const radBotNodeData = {
      intPkRadBotNodeID: selectedRadBotNode.intPkRadBotNodeID,
      radBotName: values.radBotName.trim(),
      radBotIP: values.radBotIP.trim(),
      radBotAuthPort: Number(values.radBotAuthPort),
      radBotAcctPort: Number(values.radBotAcctPort),
    };
    updateRadBotNodeData(radBotNodeData as any);
  };

  const showUpdateModal = (radBotNode: RadBotNodeTableDataType) => {
    setSelectedRadBotNode(radBotNode);
    updateForm.setFieldsValue({
      radBotName: radBotNode.radBotName,
      radBotIP: radBotNode.radBotIP,
      radBotAuthPort: radBotNode.radBotAuthPort,
      radBotAcctPort: radBotNode.radBotAcctPort,
    });
    setIsUpdateModalOpen(true);
  };

  const handleCreateCancel = () => {
    if (!createRadBotNodeLoading) {
      setIsCreateModalOpen(false);
      createForm.resetFields();
    }
  };

  const handleUpdateCancel = () => {
    if (!updateRadBotNodeLoading) {
      setIsUpdateModalOpen(false);
      updateForm.resetFields();
      setSelectedRadBotNode(null);
    }
  };

  const columns: ColumnsType<RadBotNodeTableDataType> = [
    {
      title: 'RadBot Name',
      dataIndex: 'radBotName',
      key: 'radBotName',
      render: (text: string) => <Typography.Text strong>{text || '-'}</Typography.Text>,
    },
    {
      title: 'IP Address',
      dataIndex: 'radBotIP',
      key: 'radBotIP',
      render: (text: string) => text || '-',
    },
    {
      title: 'Auth Port',
      dataIndex: 'radBotAuthPort',
      key: 'radBotAuthPort',
      render: (port: number) => port || '-',
    },
    {
      title: 'Acct Port',
      dataIndex: 'radBotAcctPort',
      key: 'radBotAcctPort',
      render: (port: number) => port || '-',
    },
    {
      title: 'Date Created',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (date: string) => (date ? formatDate(date) : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => showUpdateModal(record)}>
            Update
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}
        >
          <Typography.Title level={2}>RadBot Nodes</Typography.Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
            Create RadBot Node
          </Button>
        </div>

        {apiLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : allRadBotNodes.length === 0 ? (
          <Empty description="No RadBot nodes found" />
        ) : (
          <Table columns={columns} dataSource={allRadBotNodes} pagination={{ pageSize: 10 }} />
        )}
      </div>

      {/* Create Modal */}
      <Modal
        title="Create RadBot Node"
        open={isCreateModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
        maskClosable={!createRadBotNodeLoading}
        closable={!createRadBotNodeLoading}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>RadBot Name</span>}
            name="radBotName"
            rules={[
              { required: true, message: 'Please enter RadBot name' },
              {
                max: 45,
                message: 'RadBot name must be less than 45 characters',
              },
            ]}
          >
            <Input placeholder="Enter RadBot name" disabled={createRadBotNodeLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>RadBot IP</span>}
            name="radBotIP"
            rules={[
              { required: true, message: 'Please enter RadBot IP' },
              {
                max: 45,
                message: 'RadBot IP must be less than 45 characters',
              },
            ]}
          >
            <Input placeholder="Enter RadBot IP address" disabled={createRadBotNodeLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Auth Port</span>}
            name="radBotAuthPort"
            rules={[
              { required: true, message: 'Please enter auth port' },
              { type: 'number', min: 1, max: 65535, message: 'Port must be between 1 and 65535' },
            ]}
          >
            <InputNumber
              placeholder="Enter auth port (1024-65535)"
              disabled={createRadBotNodeLoading}
              style={{ width: '100%' }}
              min={1}
              max={65535}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Acct Port</span>}
            name="radBotAcctPort"
            rules={[
              { required: true, message: 'Please enter acct port' },
              { type: 'number', min: 1024, max: 65535, message: 'Acct port must be between 1024 and 65535' },
            ]}
          >
            <InputNumber
              placeholder="Enter acct port (1024-65535)"
              disabled={createRadBotNodeLoading}
              style={{ width: '100%' }}
              min={1024}
              max={65535}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCreateCancel} style={{ marginRight: '8px' }} disabled={createRadBotNodeLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createRadBotNodeLoading}
              disabled={createRadBotNodeLoading}
            >
              {createRadBotNodeLoading ? 'Creating...' : 'Create'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Update Modal */}
      <Modal
        title="Update RadBot Node"
        open={isUpdateModalOpen}
        onCancel={handleUpdateCancel}
        footer={null}
        width={600}
        maskClosable={!updateRadBotNodeLoading}
        closable={!updateRadBotNodeLoading}
      >
        <Form form={updateForm} layout="vertical" onFinish={handleUpdate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>RadBot Name</span>}
            name="radBotName"
            rules={[
              { required: true, message: 'Please enter RadBot name' },
              {
                max: 45,
                message: 'RadBot name must be less than 45 characters',
              },
            ]}
          >
            <Input placeholder="Enter RadBot name" disabled={updateRadBotNodeLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>RadBot IP</span>}
            name="radBotIP"
            rules={[
              { required: true, message: 'Please enter RadBot IP' },
              {
                max: 45,
                message: 'RadBot IP must be less than 45 characters',
              },
            ]}
          >
            <Input placeholder="Enter RadBot IP address" disabled={updateRadBotNodeLoading} maxLength={45} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Auth Port</span>}
            name="radBotAuthPort"
            rules={[
              { required: true, message: 'Please enter auth port' },
              { type: 'number', min: 1, max: 65535, message: 'Port must be between 1 and 65535' },
            ]}
          >
            <InputNumber
              placeholder="Enter auth port (1-65535)"
              disabled={updateRadBotNodeLoading}
              style={{ width: '100%' }}
              min={1}
              max={65535}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Acct Port</span>}
            name="radBotAcctPort"
            rules={[
              { required: true, message: 'Please enter acct port' },
              { type: 'number', min: 1024, max: 65535, message: 'Acct port must be between 1024 and 65535' },
            ]}
          >
            <InputNumber
              placeholder="Enter acct port (1024-65535)"
              disabled={updateRadBotNodeLoading}
              style={{ width: '100%' }}
              min={1024}
              max={65535}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleUpdateCancel} style={{ marginRight: '8px' }} disabled={updateRadBotNodeLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateRadBotNodeLoading}
              disabled={updateRadBotNodeLoading}
            >
              {updateRadBotNodeLoading ? 'Updating...' : 'Update'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RadBotNodesComponent;
